import React, { useState, useEffect } from 'react'
import { useLanguage } from '../i18n'
import API_CONFIG from '../config/api'
import { getMockAssetData } from '../services/mockData'

// 余额显示组件
const BalanceDisplay = ({ balance, withdrawalThreshold, t }) => {
  const canWithdraw = balance >= withdrawalThreshold
  
  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-4 text-center">{t('assetDashboard.myAssets')}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="text-center p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/20">
          <div className="text-4xl font-bold text-green-400 mb-2">{balance.toFixed(2)}</div>
          <div className="text-lg text-gray-300">{t('assetDashboard.usdtBalance')}</div>
        </div>
        
        <div className={`text-center p-6 rounded-2xl border ${
          canWithdraw 
            ? 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20' 
            : 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20'
        }`}>
          <div className="text-3xl font-bold mb-2">
            {canWithdraw ? (
              <span className="text-blue-400">{t('assetDashboard.canWithdraw')}</span>
            ) : (
              <span className="text-yellow-400">{t('assetDashboard.charging')}</span>
            )}
          </div>
          <div className="text-sm text-gray-300">
            {canWithdraw ? t('assetDashboard.thresholdReached') : t('assetDashboard.stillNeed').replace('{amount}', (withdrawalThreshold - balance).toFixed(2))}
          </div>
        </div>
      </div>
      
      <div className="mt-4 text-center">
        <div className="text-sm text-gray-400">
          {t('assetDashboard.withdrawalThreshold').replace('{amount}', withdrawalThreshold)}
        </div>
      </div>
    </div>
  )
}

// 提现申请组件
const WithdrawalPanel = ({ balance, withdrawalThreshold, onWithdraw, t }) => {
  const [toAddress, setToAddress] = useState('')
  const [amount, setAmount] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const canWithdraw = balance >= withdrawalThreshold
  const maxAmount = Math.max(0, balance)

  const handleWithdraw = async () => {
    if (!canWithdraw) {
      setError(t('assetDashboard.needThreshold').replace('{amount}', withdrawalThreshold))
      return
    }

    if (!toAddress.trim() || !/^0x[a-fA-F0-9]{40}$/.test(toAddress.trim())) {
      setError(t('assetDashboard.invalidAddress'))
      return
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError(t('assetDashboard.invalidAmount'))
      return
    }

    if (parseFloat(amount) > maxAmount) {
      setError(t('assetDashboard.amountExceeds'))
      return
    }

    if (parseFloat(amount) < withdrawalThreshold) {
      setError(t('assetDashboard.minWithdrawal').replace('{amount}', withdrawalThreshold))
      return
    }

    setIsLoading(true)
    setError('')

    try {
      await onWithdraw(toAddress.trim(), parseFloat(amount))
      setToAddress('')
      setAmount('')
    } catch (error) {
      console.error('提现失败:', error)
      setError(error.message || t('assetDashboard.withdrawalFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-4 text-center">{t('assetDashboard.usdtWithdrawal')}</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('assetDashboard.withdrawalAddress')}
          </label>
          <input
            type="text"
            value={toAddress}
            onChange={(e) => setToAddress(e.target.value)}
            placeholder={t('assetDashboard.withdrawalAddressPlaceholder')}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('assetDashboard.withdrawalAmount')}
          </label>
          <div className="flex space-x-2">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('assetDashboard.withdrawalAmountPlaceholder').replace('{min}', withdrawalThreshold).replace('{max}', maxAmount.toFixed(2))}
              min={withdrawalThreshold}
              max={maxAmount}
              step="0.01"
              className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
            />
            <button
              onClick={() => setAmount(maxAmount.toFixed(2))}
              className="px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-600/50 transition-colors"
            >
              {t('assetDashboard.all')}
            </button>
          </div>
        </div>
        
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        
        <button
          onClick={handleWithdraw}
          disabled={isLoading || !canWithdraw}
          className={`w-full py-4 rounded-2xl font-semibold text-lg shadow-lg transition-all duration-300 transform ${
            canWithdraw 
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:shadow-xl hover:scale-105' 
              : 'bg-gradient-to-r from-gray-600 to-gray-700 cursor-not-allowed'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isLoading ? t('assetDashboard.submitting') : (
            canWithdraw ? t('assetDashboard.submitWithdrawal') : t('assetDashboard.needThreshold').replace('{amount}', withdrawalThreshold)
          )}
        </button>
        
        <div className="text-center text-gray-400 text-sm space-y-1">
          <p>{t('assetDashboard.withdrawalTip1')}</p>
          <p>{t('assetDashboard.withdrawalTip2')}</p>
        </div>
      </div>
    </div>
  )
}

// 交易历史组件
const TransactionHistory = ({ transactions, t }) => {
  if (!transactions || transactions.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <h3 className="text-2xl font-bold mb-4 text-center">{t('assetDashboard.transactionHistory')}</h3>
        <p className="text-gray-400 text-center py-8">{t('assetDashboard.noTransactions')}</p>
      </div>
    )
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400'
      case 'pending': return 'text-yellow-400'
      case 'rejected': return 'text-red-400'
      default: return 'text-gray-400'
    }
  }

  const getTypeIcon = (type) => {
    return type === 'deposit' ? '💰' : '💸'
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return t('assetDashboard.completed')
      case 'pending': return t('assetDashboard.pending')
      case 'rejected': return t('assetDashboard.rejected')
      default: return status
    }
  }

  const getTypeText = (type) => {
    return type === 'deposit' ? t('assetDashboard.rewardIncome') : t('assetDashboard.withdrawalRequest')
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-4 text-center">{t('assetDashboard.transactionHistory')}</h3>
      
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {transactions.map((tx, index) => (
          <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-2xl border border-gray-600/30">
            <div className="flex items-center space-x-4">
              <span className="text-2xl">{getTypeIcon(tx.transaction_type)}</span>
              
              <div>
                <div className="font-semibold">
                  {getTypeText(tx.transaction_type)}
                </div>
                <div className="text-sm text-gray-400">
                  {tx.source} • {new Date(tx.created_at).toLocaleDateString()}
                </div>
                {tx.description && (
                  <div className="text-xs text-gray-500 mt-1">{tx.description}</div>
                )}
              </div>
            </div>
            
            <div className="text-right">
              <div className={`text-lg font-bold ${
                tx.transaction_type === 'deposit' ? 'text-green-400' : 'text-red-400'
              }`}>
                {tx.transaction_type === 'deposit' ? '+' : '-'}{tx.amount} USDT
              </div>
              <div className={`text-sm ${getStatusColor(tx.status)}`}>
                {getStatusText(tx.status)}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-center text-gray-400 text-sm">
        <p>{t('assetDashboard.showRecent').replace('{count}', transactions.length)}</p>
      </div>
    </div>
  )
}

// 奖励来源说明组件
const RewardSources = ({ t }) => {
  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-4 text-center">{t('assetDashboard.rewardSources')}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl border border-yellow-500/20">
          <h4 className="font-semibold mb-2 flex items-center">
            <span className="text-yellow-400 mr-2">🎯</span>
            {t('assetDashboard.dailyLottery')}
          </h4>
          <p className="text-sm text-gray-300">
            {t('assetDashboard.dailyLotteryDesc')}
          </p>
        </div>
        
        <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20">
          <h4 className="font-semibold mb-2 flex items-center">
            <span className="text-purple-400 mr-2">🏆</span>
            {t('assetDashboard.weeklyAirdrop')}
          </h4>
          <p className="text-sm text-gray-300">
            {t('assetDashboard.weeklyAirdropDesc')}
          </p>
        </div>
        
        <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/20">
          <h4 className="font-semibold mb-2 flex items-center">
            <span className="text-green-400 mr-2">⚡</span>
            {t('assetDashboard.interstellarExploration')}
          </h4>
          <p className="text-sm text-gray-300">
            {t('assetDashboard.interstellarExplorationDesc')}
          </p>
        </div>
        
        <div className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/20">
          <h4 className="font-semibold mb-2 flex items-center">
            <span className="text-blue-400 mr-2">👥</span>
            {t('assetDashboard.teamBonus')}
          </h4>
          <p className="text-sm text-gray-300">
            {t('assetDashboard.teamBonusDesc')}
          </p>
        </div>
      </div>
    </div>
  )
}

// 主资产管理仪表盘组件
const AssetDashboard = ({ isConnected, walletAddress }) => {
  const { t } = useLanguage()
  const [balance, setBalance] = useState(0)
  const [withdrawalThreshold, setWithdrawalThreshold] = useState(30)
  const [transactions, setTransactions] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadAssetData()
    
    // 设置定时刷新
    const interval = setInterval(() => {
      loadAssetData(false)
    }, 30000) // 30秒刷新一次

    return () => clearInterval(interval)
  }, [isConnected, walletAddress])

  const loadAssetData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true)
      
      // 检查是否使用模拟数据
      if (!API_CONFIG.hasBackend()) {
        if (isConnected && walletAddress) {
          const mockData = getMockAssetData()
          setBalance(mockData.balance)
          setWithdrawalThreshold(mockData.withdrawalThreshold)
          setTransactions(mockData.transactions)
        } else {
          setBalance(0)
          setTransactions([])
        }
        if (showLoading) setIsLoading(false)
        return
      }
      
      if (isConnected && walletAddress) {
        // 获取用户余额
        const balanceResponse = await fetch(`${API_CONFIG.baseURL}/assets/balance/${walletAddress}`)
        const balanceData = await balanceResponse.json()
        
        if (balanceData.success) {
          setBalance(balanceData.data.balance)
          setWithdrawalThreshold(balanceData.data.withdrawalThreshold)
        }

        // 获取交易历史
        const txResponse = await fetch(`${API_CONFIG.baseURL}/assets/transactions/${walletAddress}?limit=10`)
        const txData = await txResponse.json()
        
        if (txData.success) {
          setTransactions(txData.data.transactions)
        }
      } else {
        // 未连接钱包时重置数据
        setBalance(0)
        setTransactions([])
      }

    } catch (error) {
      console.error('加载资产数据失败:', error)
      // 出错时使用模拟数据
      if (isConnected && walletAddress) {
        const mockData = getMockAssetData()
        setBalance(mockData.balance)
        setWithdrawalThreshold(mockData.withdrawalThreshold)
        setTransactions(mockData.transactions)
      }
    } finally {
      if (showLoading) setIsLoading(false)
    }
  }

  const handleWithdraw = async (toAddress, amount) => {
    if (!isConnected || !walletAddress) {
      throw new Error(t('assetDashboard.pleaseConnectWallet'))
    }

    try {
      // 生成签名
      const signData = { 
        walletAddress, 
        amount, 
        toAddress, 
        timestamp: Date.now() 
      }
      
      // 在实际应用中，这里应该使用钱包签名
      // 为了演示，我们使用一个简单的签名模拟
      const signature = 'demo_signature_' + Date.now()

      const response = await fetch('http://localhost:3001/api/assets/withdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          walletAddress,
          amount,
          toAddress,
          signature
        })
      })

      const data = await response.json()
      
      if (data.success) {
        alert(t('assetDashboard.withdrawalSuccess'))
        await loadAssetData(false)
      } else {
        throw new Error(data.error || t('assetDashboard.withdrawalFailed'))
      }
    } catch (error) {
      console.error('提现失败:', error)
      throw error
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-gray-300">{t('assetDashboard.loadingAssetData')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 余额显示 */}
      <BalanceDisplay 
        balance={balance} 
        withdrawalThreshold={withdrawalThreshold} 
        t={t}
      />

      {isConnected ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 提现面板 */}
          <WithdrawalPanel
            balance={balance}
            withdrawalThreshold={withdrawalThreshold}
            onWithdraw={handleWithdraw}
            t={t}
          />
          
          {/* 交易历史 */}
          <TransactionHistory transactions={transactions} t={t} />
        </div>
      ) : (
        <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-8 shadow-2xl text-center">
          <h3 className="text-2xl font-bold mb-4">{t('assetDashboard.connectWalletToView')}</h3>
          <p className="text-gray-400">{t('assetDashboard.connectWalletDesc')}</p>
        </div>
      )}

      {/* 奖励来源说明 */}
      <RewardSources t={t} />
    </div>
  )
}

export default AssetDashboard
